import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Edit3,
    Trash2,
    X,
    Calendar,
    Clock,
    MapPin,
    Users,
    Tag,
    Shield,
    Save,
    Bell,
} from 'lucide-react';
import { useAppState, CalendarEvent } from '../../contexts/AppContext';

// Event type options
const EVENT_TYPES: { value: CalendarEvent['type']; label: string; color: string }[] = [
    { value: 'meeting', label: 'Meeting', color: '#0D5F6B' },
    { value: 'task', label: 'Task', color: '#F59E0B' },
    { value: 'focus', label: 'Focus Time', color: '#8B5CF6' },
    { value: 'personal', label: 'Personal', color: '#10B981' },
];

export default function AppEventDetail() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { events, updateEvent, deleteEvent } = useAppState();

    // Find the event
    const event = events.find(e => e.id === id);

    // Editing state
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editTimeStart, setEditTimeStart] = useState('');
    const [editTimeEnd, setEditTimeEnd] = useState('');
    const [editType, setEditType] = useState<CalendarEvent['type']>('meeting');
    const [editLocation, setEditLocation] = useState('');
    const [editAttendees, setEditAttendees] = useState<number>(0);
    const [editIsProtected, setEditIsProtected] = useState(false);
    const [editReminder, setEditReminder] = useState<number | undefined>();

    // Initialize edit form when event changes
    useEffect(() => {
        if (event) {
            setEditTitle(event.title);
            setEditDate(event.date || '');
            setEditTimeStart(event.timeStart);
            setEditTimeEnd(event.timeEnd);
            setEditType(event.type);
            setEditLocation(event.location || '');
            setEditAttendees(event.attendees || 0);
            setEditIsProtected(event.isProtected || false);
            setEditReminder(event.reminderMinutes);
        }
    }, [event]);

    // Handle not found
    if (!event) {
        return (
            <div className="min-h-screen bg-[#F5F8FA] flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-[#1A2832] mb-2">Event not found</h2>
                    <button
                        onClick={() => navigate(-1)}
                        className="text-[#0D5F6B] font-medium"
                    >
                        Go back
                    </button>
                </div>
            </div>
        );
    }

    const handleSave = () => {
        updateEvent(event.id, {
            title: editTitle,
            date: editDate || undefined,
            timeStart: editTimeStart,
            timeEnd: editTimeEnd,
            type: editType,
            location: editLocation || undefined,
            attendees: editAttendees || undefined,
            isProtected: editIsProtected,
            color: EVENT_TYPES.find(t => t.value === editType)?.color || '#0D5F6B',
            reminderMinutes: editReminder,
        });
        setIsEditing(false);
    };

    const handleDelete = () => {
        if (confirm('Are you sure you want to delete this event?')) {
            deleteEvent(event.id);
            navigate(-1);
        }
    };

    const getTypeInfo = (type: CalendarEvent['type']) => {
        return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0];
    };

    const typeInfo = getTypeInfo(event.type);

    // Format date for display
    const formatDate = (dateStr: string) => {
        if (!dateStr) return 'No date set';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Calculate duration
    const calculateDuration = () => {
        if (!event.timeStart || !event.timeEnd) return null;
        const [startH, startM] = event.timeStart.split(':').map(Number);
        const [endH, endM] = event.timeEnd.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        const durationMinutes = endMinutes - startMinutes;

        if (durationMinutes < 60) {
            return `${durationMinutes} min`;
        }
        const hours = Math.floor(durationMinutes / 60);
        const mins = durationMinutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    return (
        <div className="min-h-screen bg-[#F5F8FA] pb-24">
            {/* Header */}
            <div className="bg-white border-b border-[#DCE4EA] px-4 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-[#1A2832]" />
                        </button>
                        <h1 className="text-lg font-semibold text-[#1A2832]">Event Details</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5 text-[#7B8C9B]" />
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="p-2 hover:bg-[#D9EEF2] rounded-full transition-colors"
                                >
                                    <Save className="w-5 h-5 text-[#0D5F6B]" />
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <Edit3 className="w-5 h-5 text-[#7B8C9B]" />
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="p-2 hover:bg-red-50 rounded-full transition-colors"
                                >
                                    <Trash2 className="w-5 h-5 text-red-500" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 space-y-4">
                {/* Event Header Card */}
                <div
                    className="rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]"
                    style={{ backgroundColor: `${typeInfo.color}10` }}
                >
                    {/* Title */}
                    {isEditing ? (
                        <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full text-xl font-semibold text-[#1A2832] border-b-2 border-[#0D5F6B] outline-none bg-transparent pb-1 mb-3"
                            autoFocus
                        />
                    ) : (
                        <h2 className="text-xl font-semibold text-[#1A2832] mb-3">
                            {event.title}
                        </h2>
                    )}

                    {/* Type Badge */}
                    <div className="flex items-center gap-2 mb-4">
                        <Tag className="w-4 h-4" style={{ color: typeInfo.color }} />
                        {isEditing ? (
                            <select
                                value={editType}
                                onChange={(e) => setEditType(e.target.value as CalendarEvent['type'])}
                                className="text-sm font-medium px-3 py-1 rounded-full border border-[#DCE4EA] bg-white outline-none"
                            >
                                {EVENT_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        ) : (
                            <span
                                className="text-sm font-medium px-3 py-1 rounded-full"
                                style={{
                                    backgroundColor: `${typeInfo.color}20`,
                                    color: typeInfo.color,
                                }}
                            >
                                {typeInfo.label}
                            </span>
                        )}

                        {event.isProtected && !isEditing && (
                            <span className="flex items-center gap-1 text-sm text-[#7B8C9B]">
                                <Shield className="w-3 h-3" />
                                Protected
                            </span>
                        )}
                    </div>

                    {/* Duration Quick View */}
                    {calculateDuration() && (
                        <div className="text-sm text-[#4A5C6A]">
                            {event.timeStart} - {event.timeEnd} ({calculateDuration()})
                        </div>
                    )}
                </div>

                {/* Details Card */}
                <div className="bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                    <h3 className="text-lg font-semibold text-[#1A2832] mb-4">Details</h3>

                    <div className="space-y-3">
                        {/* Date */}
                        <div className="flex items-center gap-3 p-3 bg-[#F5F8FA] rounded-xl">
                            <Calendar className="w-5 h-5 text-[#7B8C9B]" />
                            {isEditing ? (
                                <input
                                    type="date"
                                    value={editDate}
                                    onChange={(e) => setEditDate(e.target.value)}
                                    className="flex-1 bg-transparent outline-none text-sm text-[#1A2832]"
                                />
                            ) : (
                                <span className="text-sm text-[#1A2832]">
                                    {formatDate(event.date || '')}
                                </span>
                            )}
                        </div>

                        {/* Time */}
                        <div className="flex items-center gap-3 p-3 bg-[#F5F8FA] rounded-xl">
                            <Clock className="w-5 h-5 text-[#7B8C9B]" />
                            {isEditing ? (
                                <div className="flex-1 flex items-center gap-2">
                                    <input
                                        type="time"
                                        value={editTimeStart}
                                        onChange={(e) => setEditTimeStart(e.target.value)}
                                        className="bg-transparent outline-none text-sm text-[#1A2832]"
                                    />
                                    <span className="text-sm text-[#7B8C9B]">to</span>
                                    <input
                                        type="time"
                                        value={editTimeEnd}
                                        onChange={(e) => setEditTimeEnd(e.target.value)}
                                        className="bg-transparent outline-none text-sm text-[#1A2832]"
                                    />
                                </div>
                            ) : (
                                <span className="text-sm text-[#1A2832]">
                                    {event.timeStart} - {event.timeEnd}
                                </span>
                            )}
                        </div>

                        {/* Location */}
                        <div className="flex items-center gap-3 p-3 bg-[#F5F8FA] rounded-xl">
                            <MapPin className="w-5 h-5 text-[#7B8C9B]" />
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editLocation}
                                    onChange={(e) => setEditLocation(e.target.value)}
                                    placeholder="Add location..."
                                    className="flex-1 bg-transparent outline-none text-sm text-[#1A2832] placeholder:text-[#7B8C9B]"
                                />
                            ) : (
                                <span className="text-sm text-[#1A2832]">
                                    {event.location || 'No location'}
                                </span>
                            )}
                        </div>

                        {/* Attendees */}
                        <div className="flex items-center gap-3 p-3 bg-[#F5F8FA] rounded-xl">
                            <Users className="w-5 h-5 text-[#7B8C9B]" />
                            {isEditing ? (
                                <input
                                    type="number"
                                    min="0"
                                    value={editAttendees}
                                    onChange={(e) => setEditAttendees(parseInt(e.target.value) || 0)}
                                    className="w-20 bg-transparent outline-none text-sm text-[#1A2832]"
                                />
                            ) : (
                                <span className="text-sm text-[#1A2832]">
                                    {event.attendees ? `${event.attendees} attendees` : 'No attendees'}
                                </span>
                            )}
                        </div>

                        {/* Protected Toggle (only in edit mode) */}
                        {isEditing && (
                            <div className="flex items-center justify-between p-3 bg-[#F5F8FA] rounded-xl">
                                <div className="flex items-center gap-3">
                                    <Shield className="w-5 h-5 text-[#7B8C9B]" />
                                    <span className="text-sm text-[#1A2832]">Protected Time</span>
                                </div>
                                <button
                                    onClick={() => setEditIsProtected(!editIsProtected)}
                                    className={`w-12 h-6 rounded-full transition-colors ${editIsProtected ? 'bg-[#0D5F6B]' : 'bg-[#DCE4EA]'
                                        }`}
                                >
                                    <div
                                        className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${editIsProtected ? 'translate-x-6' : 'translate-x-0.5'
                                            }`}
                                    />
                                </button>
                            </div>
                        )}

                        {/* Reminder Row */}
                        <div className="flex items-center gap-3 p-3 bg-[#F5F8FA] rounded-xl">
                            <Bell className="w-5 h-5 text-[#7B8C9B]" />
                            {isEditing ? (
                                <select
                                    value={editReminder ?? ''}
                                    onChange={(e) => setEditReminder(e.target.value ? Number(e.target.value) : undefined)}
                                    className="flex-1 bg-transparent outline-none text-sm text-[#1A2832]"
                                >
                                    <option value="">No reminder</option>
                                    <option value="0">At time</option>
                                    <option value="5">5 minutes before</option>
                                    <option value="15">15 minutes before</option>
                                    <option value="30">30 minutes before</option>
                                    <option value="60">1 hour before</option>
                                    <option value="1440">1 day before</option>
                                </select>
                            ) : (
                                <span className="text-sm text-[#1A2832]">
                                    {event.reminderMinutes !== undefined ? (
                                        event.reminderMinutes === 0 ? 'At time' :
                                            event.reminderMinutes === 5 ? '5 min before' :
                                                event.reminderMinutes === 15 ? '15 min before' :
                                                    event.reminderMinutes === 30 ? '30 min before' :
                                                        event.reminderMinutes === 60 ? '1 hour before' :
                                                            event.reminderMinutes === 1440 ? '1 day before' :
                                                                `${event.reminderMinutes} min before`
                                    ) : 'No reminder'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Google Calendar Badge (if synced) */}
                {event.googleEventId && (
                    <div className="bg-white rounded-xl p-3 shadow-[0_2px_8px_rgba(13,95,107,0.06)] flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-[#4285F4] flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm text-[#4A5C6A]">Synced from Google Calendar</span>
                    </div>
                )}

                {/* Quick Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('/app/calendar')}
                        className="flex-1 py-3 bg-[#0D5F6B] text-white rounded-xl font-semibold"
                    >
                        View in Calendar
                    </button>
                    {event.type === 'meeting' && event.location && (
                        <button
                            onClick={() => window.open(`https://maps.google.com/maps?q=${encodeURIComponent(event.location || '')}`, '_blank')}
                            className="px-6 py-3 bg-white text-[#0D5F6B] border border-[#DCE4EA] rounded-xl font-semibold"
                        >
                            Directions
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
